import axios from "axios";
import { BASE_API_URL } from "../../utils/consts";
import { isLocationActive } from "../../utils/helpers/locationVisibility";
import {
  Credit,
  CreditPayment,
  CreateCreditPayload,
  ClientLocation,
  LoanEvent,
  CreateLoanEventPayload,
} from "./types";

const withAuthHeaders = (token?: string) => {
  if (!token) return {};
  return {
    headers: {
      "x-access-token": token,
    },
  };
};

export const fetchCredits = async (
  clientId: string,
  token?: string,
  includeFrozen?: boolean,
): Promise<Credit[]> => {
  const { data } = await axios.get(`${BASE_API_URL}/credits`, {
    params: { clientId, includeFrozen: includeFrozen ? "true" : "false" },
    ...withAuthHeaders(token),
  });
  return data;
};

export const fetchCreditDetails = async (
  creditId: string,
  token?: string,
): Promise<Credit> => {
  const { data } = await axios.get(
    `${BASE_API_URL}/credits/${creditId}`,
    withAuthHeaders(token),
  );
  return data;
};

export const createCredit = async (
  payload: CreateCreditPayload,
  token?: string,
): Promise<Credit> => {
  const { data } = await axios.post(
    `${BASE_API_URL}/credits`,
    payload,
    withAuthHeaders(token),
  );
  return data;
};

export const updateCredit = async (
  creditId: string,
  payload: Partial<CreateCreditPayload> & { status?: string },
  token?: string,
): Promise<Credit> => {
  const { data } = await axios.put(
    `${BASE_API_URL}/credits/${creditId}`,
    payload,
    withAuthHeaders(token),
  );
  return data;
};

export const addManualPayment = async (
  creditId: string,
  amount: number,
  token?: string,
  note?: string,
  paymentDate?: string,
): Promise<Credit> => {
  const { data } = await axios.post(
    `${BASE_API_URL}/credits/${creditId}/manual-payments`,
    { amount, note, paymentDate },
    withAuthHeaders(token),
  );
  return data;
};

export const updatePayment = async (
  creditId: string,
  paymentId: string,
  payload: Partial<CreditPayment>,
  token?: string,
): Promise<Credit> => {
  const { data } = await axios.patch(
    `${BASE_API_URL}/credits/${creditId}/payments/${paymentId}`,
    payload,
    withAuthHeaders(token),
  );
  return data;
};

export const deletePayment = async (
  creditId: string,
  paymentId: string,
  token?: string,
): Promise<Credit> => {
  const { data } = await axios.delete(
    `${BASE_API_URL}/credits/${creditId}/payments/${paymentId}`,
    withAuthHeaders(token),
  );
  return data;
};

export const markInstallmentPaid = async (
  creditId: string,
  token?: string,
): Promise<Credit> => {
  const { data } = await axios.post(
    `${BASE_API_URL}/credits/${creditId}/mark-installment`,
    {},
    withAuthHeaders(token),
  );
  return data;
};

export const deleteCredit = async (creditId: string, token?: string) => {
  await axios.delete(
    `${BASE_API_URL}/credits/${creditId}`,
    withAuthHeaders(token),
  );
};

export const freezeCredit = async (
  creditId: string,
  isFrozen: boolean,
  token?: string,
): Promise<Credit> => {
  const { data } = await axios.patch(
    `${BASE_API_URL}/credits/${creditId}/freeze`,
    { isFrozen },
    withAuthHeaders(token),
  );
  return data;
};

export const fetchLocations = async (
  clientId: string,
  token?: string,
): Promise<ClientLocation[]> => {
  const { data } = await axios.get(`${BASE_API_URL}/${clientId}/locations`, {
    ...withAuthHeaders(token),
  });
  return (data || []).filter(isLocationActive);
};

export const confirmPaymentPaid = async (
  creditId: string,
  paymentId: string,
  isPaid: boolean,
  token?: string,
): Promise<Credit> => {
  const { data } = await axios.post(
    `${BASE_API_URL}/credits/${creditId}/payments/${paymentId}/confirm-paid`,
    { isPaid },
    withAuthHeaders(token),
  );
  return data;
};

export const getUnpaidInstallmentsCount = async (
  clientId: string,
  token?: string,
): Promise<number> => {
  const { data } = await axios.get(
    `${BASE_API_URL}/credits-stats/unpaid-count`,
    {
      params: { clientId },
      ...withAuthHeaders(token),
    },
  );
  return data.unpaidCount;
};

// LoanEvent API functions
export const fetchLoanEvents = async (
  creditId: string,
  token?: string,
): Promise<LoanEvent[]> => {
  const { data } = await axios.get(
    `${BASE_API_URL}/credits/${creditId}/events`,
    withAuthHeaders(token),
  );
  return data;
};

export const createLoanEvent = async (
  creditId: string,
  payload: CreateLoanEventPayload,
  token?: string,
): Promise<LoanEvent> => {
  const { data } = await axios.post(
    `${BASE_API_URL}/credits/${creditId}/events`,
    payload,
    withAuthHeaders(token),
  );
  return data;
};

export const updateLoanEvent = async (
  creditId: string,
  eventId: string,
  payload: Partial<CreateLoanEventPayload>,
  token?: string,
): Promise<LoanEvent> => {
  const { data } = await axios.put(
    `${BASE_API_URL}/credits/${creditId}/events/${eventId}`,
    payload,
    withAuthHeaders(token),
  );
  return data;
};

export const deleteLoanEvent = async (
  creditId: string,
  eventId: string,
  token?: string,
): Promise<void> => {
  await axios.delete(
    `${BASE_API_URL}/credits/${creditId}/events/${eventId}`,
    withAuthHeaders(token),
  );
};

export const analyzeCreditImage = async (
  files: File[],
  token?: string,
): Promise<any> => {
  const formData = new FormData();
  files.forEach((file) => formData.append("images", file));
  const { data } = await axios.post(
    `${BASE_API_URL}/credits/analyze-image`,
    formData,
    {
      headers: {
        "x-access-token": token || "",
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return data;
};

export type ScheduleRowInput = {
  period: number;
  date: string;
  disbursement: number;
  otherDisbursements: number;
  principalPaid: number;
  interestPaid: number;
  otherPayments: number;
  remainingPrincipal: number;
  description?: string;
};

export const importCreditSchedule = async (
  payload: {
    clientId: string;
    name: string;
    totalAmount: number;
    annuity?: number;
    annualInterestRate?: number;
    effectiveAnnualRate?: number;
    startDate: string;
    disbursementDate?: string;
    endDate?: string;
    currency?: string;
    schedule: ScheduleRowInput[];
    locationId?: string | null;
  },
  token?: string,
): Promise<Credit> => {
  const { data } = await axios.post(
    `${BASE_API_URL}/credits/import-schedule`,
    payload,
    withAuthHeaders(token),
  );
  return data;
};

export const updateCreditSchedule = async (
  creditId: string,
  schedule: ScheduleRowInput[],
  token?: string,
): Promise<Credit> => {
  const { data } = await axios.put(
    `${BASE_API_URL}/credits/${creditId}/schedule`,
    { schedule },
    withAuthHeaders(token),
  );
  return data;
};

export const convertCreditCurrency = async (
  creditId: string,
  targetCurrency: string,
  exchangeRate: number,
  token?: string,
): Promise<Credit> => {
  const { data } = await axios.post(
    `${BASE_API_URL}/credits/${creditId}/convert-currency`,
    { targetCurrency, exchangeRate },
    withAuthHeaders(token),
  );
  return data;
};

export type { CreditPayment };
